import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { getFilterFromLocalStorage } from '@highlander/services/helpers';
import {
  Customer,
  CustomerGroupsByPartner,
  CustomerListItem,
  CustomerPartnerListItem,
  SetCustomerEntraTenantIdRequest,
} from '@models/customers';
import { PartnerToggleOptions } from '@models/partners';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { CUSTOMERS_TABLE_ID } from 'src/app/app-pages/customers/customers.component';
import { CspUtilService } from '../csp/csp-util/csp-util.service';
import {
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  findAndReplace,
  getAll,
  getOne,
  guidRegex,
  StateDictionary,
} from '../helpers';
import { CustomersService } from './customers.service';

type CustomersState = {
  customers: CustomerListItem[];
  isLoading: boolean;
  toggle: PartnerToggleOptions;

  selectedCustomer: Customer | null;
  isSelectedCustomerLoading: boolean;

  customerPartners: CustomerPartnerListItem[];
  isCustomerPartnersLoading: boolean;

  customerGroups: CustomerGroupsByPartner[];
  isCustomerGroupsLoading: boolean;
};

const initialState: CustomersState = {
  customers: [],
  isLoading: false,
  toggle: PartnerToggleOptions.Operative,

  selectedCustomer: null,
  isSelectedCustomerLoading: false,

  customerPartners: [],
  isCustomerPartnersLoading: false,

  customerGroups: [],
  isCustomerGroupsLoading: false,
};

export const customersDictionary: StateDictionary<CustomerListItem> = {
  entityList: 'customers',
  listLoading: 'isLoading',
  selectedEntity: 'selectedCustomer',
  selectedEntityLoading: 'isSelectedCustomerLoading',
  entityId: 'id',
  createLoading: '',
};

export const CustomersStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('customers-store'),

  withComputed(store => ({
    selectedCustomerId: computed(() => store.selectedCustomer()?.id || null),
  })),

  withMethods((store, customersService = inject(CustomersService), router = inject(Router), cspUtilService = inject(CspUtilService)) => {
    createStateResetHandler<CustomersState>('/customers')(store, initialState);
    createCustomStateSetOnUrlHandler<CustomersState>('/customers', { selectedCustomer: null })(store);
    createCustomStateSetOnRegexHandler<CustomersState>(new RegExp(`^/customers/(${guidRegex})/partners$`))(store, {
      customerPartners: [],
    });

    const savedFilter = getFilterFromLocalStorage(CUSTOMERS_TABLE_ID);
    if (savedFilter?.toggleSearch) {
      patchState(store, {
        toggle: savedFilter.toggleSearch === 'All' ? PartnerToggleOptions.All : PartnerToggleOptions.Operative,
      });
    }

    const customerMethods = {
      getAll: getAll<CustomerListItem, boolean | undefined>({
        store,
        service: customersService,
        dictionary: customersDictionary,
      }),

      getOne: getOne<Customer>({
        store,
        service: customersService,
        dictionary: customersDictionary,
      }),

      setEntraTenantId: rxMethod<{ customerId: string; req: SetCustomerEntraTenantIdRequest }>(
        pipe(
          tap(() => patchState(store, { isLoading: true, isSelectedCustomerLoading: true })),
          switchMap(({ customerId, req }) =>
            customersService.setEntraTenantId(customerId, req).pipe(
              tap(customer => {
                patchState(store, {
                  isLoading: false,
                  isSelectedCustomerLoading: false,
                  customers: findAndReplace<CustomerListItem>(store.customers(), customer, item => item.id === customer.id),
                  selectedCustomer: customer,
                });
              }),
              catchError(() => {
                patchState(store, { isLoading: false, isSelectedCustomerLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getCustomerPartners: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isCustomerPartnersLoading: true })),
          switchMap(customerId =>
            customersService.getCustomerPartners(customerId).pipe(
              tap(partners => {
                patchState(store, {
                  customerPartners: partners,
                  isCustomerPartnersLoading: false,
                });
              }),
              catchError(() => {
                patchState(store, { isCustomerPartnersLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      getCustomerGroups: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isCustomerGroupsLoading: true })),
          switchMap(customerId =>
            customersService.getCustomerGroups(customerId).pipe(
              tap(groups => {
                patchState(store, {
                  isCustomerGroupsLoading: false,
                  customerGroups: groups.sort((a, b) => a.partnerName.localeCompare(b.partnerName)),
                });
              }),
              catchError(() => {
                patchState(store, { isCustomerGroupsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      refreshSubscriptions: rxMethod<{ customerTenantId: string; customerId: string }>(
        pipe(
          tap(() => patchState(store, { isSelectedCustomerLoading: true })),
          switchMap(({ customerTenantId, customerId }) =>
            cspUtilService.refreshCustomerSubscriptions(customerTenantId).pipe(
              tap(() => {
                patchState(store, { isSelectedCustomerLoading: false });
                customerMethods.getOne(customerId);
              }),
              catchError(() => {
                patchState(store, { isSelectedCustomerLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const navigationActions = {
      goToCustomerActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['customers', id]);
        if (!result) return false;
        patchState(store, { customerPartners: [], customerGroups: [] });
        return result;
      },

      goToCustomerList: (): void => {
        patchState(store, { selectedCustomer: null });
        router.navigate(['customers']);
      },
    };

    const utilityMethods = {
      loadCustomers: (toggle?: PartnerToggleOptions) => {
        if (toggle !== undefined) {
          patchState(store, { toggle });
        }
        const currentToggle = store.toggle();
        customerMethods.getAll(currentToggle === PartnerToggleOptions.All);
      },
    };

    return {
      ...customerMethods,
      ...navigationActions,
      ...utilityMethods,
    };
  }),
);

export interface CustomersStoreType {
  // State properties
  readonly customers: Signal<CustomerListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly toggle: Signal<PartnerToggleOptions>;

  readonly selectedCustomer: Signal<Customer | null>;
  readonly isSelectedCustomerLoading: Signal<boolean>;

  readonly customerPartners: Signal<CustomerPartnerListItem[]>;
  readonly isCustomerPartnersLoading: Signal<boolean>;

  readonly customerGroups: Signal<CustomerGroupsByPartner[]>;
  readonly isCustomerGroupsLoading: Signal<boolean>;

  readonly selectedCustomerId: Signal<string | null>;

  // Customer methods
  readonly getAll: (input: boolean | undefined) => void;
  readonly getOne: (id: string) => void;
  readonly setEntraTenantId: (params: { customerId: string; req: SetCustomerEntraTenantIdRequest }) => void;
  readonly getCustomerPartners: (customerId: string) => void;
  readonly getCustomerGroups: (customerId: string) => void;
  readonly refreshSubscriptions: (params: { customerTenantId: string; customerId: string }) => void;

  // Navigation methods
  readonly goToCustomerActions: (id: string) => Promise<boolean>;
  readonly goToCustomerList: () => void;

  // Utility methods
  readonly loadCustomers: (toggle?: PartnerToggleOptions) => void;
}
